import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { categories, mainSiteUrl } from '@/mocks/categories';
import LanguageSwitcher from './LanguageSwitcher';

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const goToSection = (id: string) => {
    setMobileOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }, 120);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const isHome = location.pathname === '/';
  const overHero = isHome && !scrolled && !mobileOpen;

  const linkColor = overHero
    ? 'text-white/80 hover:text-white'
    : 'text-foreground-600 hover:text-foreground-900';
  const brandColor = overHero ? 'text-white' : 'text-foreground-950';
  const menuIconColor = overHero ? 'text-white' : 'text-foreground-800';

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled || mobileOpen
          ? 'bg-white/95 backdrop-blur-md border-b border-background-200/70 shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <nav className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap cursor-pointer">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-white">
            <i className="ri-grid-line text-lg"></i>
          </span>
          <span className={`font-heading text-base font-bold md:text-lg ${brandColor}`}>
            {t('nav.brand')}
          </span>
        </Link>

        <div className="hidden items-center gap-7 md:flex">
          <Link
            to="/"
            className={`text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${
              isHome ? 'text-primary-600' : linkColor
            }`}
          >
            {t('nav.home')}
          </Link>
          <button
            type="button"
            onClick={() => goToSection('categories')}
            className={`text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${linkColor}`}
          >
            {t('nav.categories')}
          </button>
          <button
            type="button"
            onClick={() => goToSection('guides')}
            className={`text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${linkColor}`}
          >
            {t('nav.shoppingGuide')}
          </button>
          <button
            type="button"
            onClick={() => goToSection('faq')}
            className={`text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${linkColor}`}
          >
            {t('nav.faq')}
          </button>
        </div>

        <div className="hidden items-center gap-3 md:flex">
          <LanguageSwitcher />
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600 cursor-pointer"
          >
            {t('nav.sheetCta')}
          </a>
        </div>

        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          className={`flex h-10 w-10 items-center justify-center rounded-lg md:hidden cursor-pointer ${menuIconColor}`}
          aria-label="Toggle menu"
        >
          <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
        </button>
      </nav>

      {mobileOpen && (
        <div className="border-t border-background-200 bg-white px-4 py-4 md:hidden">
          <div className="flex flex-col gap-1">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-semibold text-foreground-800 hover:bg-background-100 cursor-pointer"
            >
              <span className="flex h-6 w-6 items-center justify-center text-primary-600">
                <i className="ri-home-4-line"></i>
              </span>
              {t('nav.home')}
            </Link>
            <button
              type="button"
              onClick={() => goToSection('categories')}
              className="flex items-center gap-3 rounded-lg px-3 py-3 text-left text-sm font-semibold text-foreground-800 hover:bg-background-100 cursor-pointer"
            >
              <span className="flex h-6 w-6 items-center justify-center text-primary-600">
                <i className="ri-price-tag-3-line"></i>
              </span>
              {t('nav.categories')}
            </button>
            <button
              type="button"
              onClick={() => goToSection('guides')}
              className="flex items-center gap-3 rounded-lg px-3 py-3 text-left text-sm font-semibold text-foreground-800 hover:bg-background-100 cursor-pointer"
            >
              <span className="flex h-6 w-6 items-center justify-center text-primary-600">
                <i className="ri-book-read-line"></i>
              </span>
              {t('nav.shoppingGuide')}
            </button>
            <button
              type="button"
              onClick={() => goToSection('faq')}
              className="flex items-center gap-3 rounded-lg px-3 py-3 text-left text-sm font-semibold text-foreground-800 hover:bg-background-100 cursor-pointer"
            >
              <span className="flex h-6 w-6 items-center justify-center text-primary-600">
                <i className="ri-question-line"></i>
              </span>
              {t('nav.faq')}
            </button>
          </div>

          <p className="mt-4 px-3 text-xs font-semibold uppercase tracking-wide text-foreground-400">
            {t('categories.title')}
          </p>
          <div className="mt-2 grid grid-cols-2 gap-1.5">
            {categories.map((cat) => (
              <a
                key={cat.id}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 rounded-lg px-2 py-2 text-sm text-foreground-700 hover:bg-background-100 cursor-pointer"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-500">
                  <i className={`${cat.icon} text-base`}></i>
                </span>
                <span className="truncate">{t(`categories.${cat.id}`, cat.name)}</span>
              </a>
            ))}
          </div>

          <div className="mt-4 flex items-center gap-3">
            <LanguageSwitcher />
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="flex-1 rounded-full bg-primary-500 px-4 py-2 text-center text-sm font-semibold text-white cursor-pointer"
            >
              {t('nav.sheetCta')}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}